import { MENT_MAPPINGS } from "@/configuration/document-mappings";
import entpInsuranceMappingData from "@/configuration/groups/entpartners/insurance-mapping.json";
import {
  buildPatientMetadataInsuranceVariables,
  resolveCoverageEcwId,
  type EcwInsuranceMapping,
  type EcwInsuranceMappingEntry,
  type PatientMetadataInsuranceVariables,
} from "@/src/lib/ecw-insurance-mapping";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { Classification, ClassificationSection } from "@/src/models/constants/classifications";
import { DefaultWorkflow, Workflow, WorkflowType } from "@/src/models/constants/group";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Coverage } from "@/src/models/coverage";
import type { Referral } from "@/src/models/documents/referral";
import { Group } from "@/src/models/group";
import { PersonLanguage } from "@/src/models/person";
import { Section } from "@/src/models/section";
import { TaskStatus } from "@/src/models/task";

export const MENT_DOCUMENT_UPLOAD_PRACTITIONER_ID = "RixqkULqC-poxGyOQ1ZTeTd9EEP-QMZTlQYbnoeidJo";

/**
 * Returns the upload classification for the MENT group
 */
export function getMentUploadClassification(classification: Classification): Classification {
  switch (classification) {
    case Classification.COVER_PAGE:
    case Classification.INSURANCE_CARD:
    case Classification.INTAKE:
    case Classification.IDENTIFICATION_CARD:
      return Classification.REFERRAL;
    default:
      return classification;
  }
}

/**
 * Returns the classifications to extract for the MENT group
 */
export function getMentClassificationToExtract(): Classification[] {
  return [Classification.REFERRAL];
}

/**
 * Returns the classifications for document upload for the MENT group
 */
export function getMentClassificationForDocumentUpload(): Classification[] {
  return (Object.keys(MENT_MAPPINGS) as Classification[]).filter((c) => c !== Classification.PRIOR_AUTHORIZATION);
}

/**
 * Returns the workflows for the MENT group
 */
export async function getMentWorkflows(group: Group): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    ...DefaultWorkflow,
    [Workflow.PRIOR_AUTHORIZATION]: [Classification.PRIOR_AUTHORIZATION],
    [Workflow.DOC_MANAGEMENT]: getMentClassificationForDocumentUpload(),
    [Workflow.LEAD_FORM]: [Classification.ADMIN_LEAD_FORM],
  };
}

const MENT_SERVICE_LINES = [
  ServiceLine.AUDIOLOGY,
  ServiceLine.ENT,
  ServiceLine.SLEEP,
  ServiceLine.TONSIL,
  ServiceLine.SINUS,
] as const;

const MENT_HOLIDAYS = [
  HolidayNames.NEW_YEARS_DAY,
  HolidayNames.MEMORIAL_DAY,
  HolidayNames.INDEPENDENCE_DAY,
  HolidayNames.THANKSGIVING,
  HolidayNames.BLACK_FRIDAY,
  HolidayNames.CHRISTMAS_EVE,
  HolidayNames.CHRISTMAS_DAY,
  HolidayNames.NEW_YEARS_EVE,
] as const;

/**
 * Returns the holidays for the MENT group
 */
export function getMentHolidays(): HolidayNames[] {
  return [...MENT_HOLIDAYS];
}

const mentClassificationsForDisplay = [
  Classification.ADMIN_LEAD_FORM,
  Classification.BLANK,
  Classification.CARE_PLAN,
  Classification.CONSENT,
  Classification.COVER_PAGE,
  Classification.DME_ORDER,
  Classification.FINANCIAL,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_EKG,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.LEGAL,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.OTHER,
  Classification.PHARMACY_RELATED,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PROCEDURE_INVENTORY_LIST,
  Classification.PROCEDURE_SLEEP,
  Classification.PROGRESS_NOTE,
  Classification.REFERRAL,
  Classification.REFERRAL_STATUS_REQUEST,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
  Classification.UNKNOWN,
] as Classification[];

export function getMentClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classifications",
      options: mentClassificationsForDisplay,
    },
  ];
}

export function getMentScheduleConfigPath(): string {
  return "configuration/groups/entpartners/rules/schedule.yaml";
}

export function getMentAutocloseStatus(section: Section): TaskStatus {
  const classification = section.uploadClassification || section.classification;

  if (classification === Classification.REFERRAL || classification === Classification.PRIOR_AUTHORIZATION) {
    return TaskStatus.VERIFICATION_NEEDED;
  }

  return TaskStatus.REVIEW_NEEDED;
}

export type MentInsuranceMappingEntry = EcwInsuranceMappingEntry;
export type MentInsuranceMapping = EcwInsuranceMapping;

export const MENT_INSURANCE_MAPPING: MentInsuranceMapping = entpInsuranceMappingData as MentInsuranceMapping;

function resolveMentCoverageEcwId(coverage: Coverage | null, forPrimary: boolean): string | null {
  return resolveCoverageEcwId(coverage, MENT_INSURANCE_MAPPING, forPrimary);
}

export type MentPatientMetadataInsuranceVariables = PatientMetadataInsuranceVariables;

export function getMentPatientMetadataInsuranceVariables(
  referral: Referral | null
): MentPatientMetadataInsuranceVariables {
  const primary = referral?.primaryCoverage ?? null;
  const secondary = referral?.secondaryCoverage ?? null;
  const primaryEcwId = resolveMentCoverageEcwId(primary, true);
  const secondaryEcwId = resolveMentCoverageEcwId(secondary, false);

  return buildPatientMetadataInsuranceVariables(primary, secondary, primaryEcwId, secondaryEcwId);
}

export const MENT_DEFAULT_LANGUAGES = new Set([
  PersonLanguage.ENGLISH.toLowerCase(),
  PersonLanguage.NOT_PROVIDED.toLowerCase(),
]);

export const MENT_REFERRAL_TEL_ENC_ASSIGNMENT_DEFAULT = "User, Scheduler";
export const MENT_REFERRAL_TEL_ENC_ASSIGNMENT_SUB = "SUB Sanchez, Rafaela";
export const MENT_DEMOGRAPHICS_TEL_ENC_ASSIGNMENT = "User, Front Desk";

export const MENT_ECW_LOGIN_URL = "https://mdetcepr8mqion1hfqapp.ecwcloud.com/mobiledoc/jsp/webemr/login/newLogin.jsp";
export const MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE = "FRONT,OFFICE STAFF";
export const MENT_REFERRAL_FACILITY = "Maryland ENT Center";

export const MENT_PROVIDER_NPIS_SUB = new Set(["1588753693", "1356350953", "1043752918"]);

export const MENT_ADDRESS_SIMILARITY_THRESHOLD = 0.85;
